import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  ArrowRightLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  Pencil,
  Phone,
  Trash2,
  X,
} from 'lucide-react-native';
import { useCallback, useEffect, useState } from 'react';
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import * as DocumentPicker from 'expo-document-picker';

import { useConfirm, useNotice } from '@/components/ConfirmDialog';
import { DocumentPreview } from '@/components/DocumentPreview';
import { PdfThumb } from '@/components/PdfThumb';
import { StickyBottomBar } from '@/components/StickyBottomBar';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type { ContactRow, DocumentRow, QuoteRow, QuoteStatus } from '@/db/types';
import { getContactById } from '@/lib/contacts';
import { addDocument, getDocumentById } from '@/lib/documents';
import { persistPickedFile } from '@/lib/files';
import { goBackOrTo } from '@/lib/nav';
import {
  QUOTE_STATUS_META,
  convertQuoteToExpense,
  deleteQuote,
  getQuoteById,
  listQuotes,
  updateQuote,
} from '@/lib/quotes';
import { listGoals } from '@/lib/goals';
import { getActiveProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

/**
 * Quote detail. The status row is the lifecycle control — tap a chip
 * to mark accepted / declined / etc. "Converted" is never manually
 * settable: it only happens through the Convert button, which creates
 * the expense and links the two records.
 */
export default function QuoteDetail() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const db = useSQLiteContext();
  const confirm = useConfirm();
  const notice = useNotice();
  const [quote, setQuote] = useState<QuoteRow | null>(null);
  const [doc, setDoc] = useState<DocumentRow | null>(null);
  const [contact, setContact] = useState<ContactRow | null>(null);
  const [working, setWorking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Inline edit mode — same pattern as the expense detail page, so a
  // typo'd amount or vendor doesn't force delete-and-re-enter (which
  // would destroy the price-history record).
  const [editing, setEditing] = useState(false);
  const [eAmount, setEAmount] = useState('');
  const [eProject, setEProject] = useState('');
  const [eVendor, setEVendor] = useState('');
  const [eDescription, setEDescription] = useState('');
  const [eNotes, setENotes] = useState('');
  // Project suggestion pool (Wave 72) — mirrors quotes/new.tsx.
  const [projectPool, setProjectPool] = useState<string[]>([]);

  const fetch = useCallback(async () => {
    if (!id) return;
    const q = await getQuoteById(db, id);
    setQuote(q);
    setLoading(false);
    if (q?.quote_doc_id) {
      setDoc(await getDocumentById(db, q.quote_doc_id));
    } else {
      setDoc(null);
    }
    if (q?.contact_id) {
      setContact(await getContactById(db, q.contact_id));
    } else {
      setContact(null);
    }
  }, [db, id]);

  useFocusEffect(
    useCallback(() => {
      fetch();
    }, [fetch])
  );

  useEffect(() => {
    (async () => {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      const [quotes, goals] = await Promise.all([listQuotes(db, prop.id), listGoals(db, prop.id)]);
      const names: string[] = [];
      for (const q of quotes) {
        const pj = q.project?.trim();
        if (pj && !names.some((n) => n.toLowerCase() === pj.toLowerCase())) names.push(pj);
      }
      for (const g of goals) {
        if (g.status === 'done') continue;
        const t = g.title.trim();
        if (t && !names.some((n) => n.toLowerCase() === t.toLowerCase())) names.push(t);
      }
      setProjectPool(names);
    })();
  }, [db]);

  const projectSuggestions = (() => {
    const q = eProject.trim().toLowerCase();
    if (!q) return projectPool.slice(0, 6);
    if (projectPool.some((n) => n.toLowerCase() === q)) return [];
    return projectPool.filter((n) => n.toLowerCase().includes(q)).slice(0, 6);
  })();

  if (!quote) {
    return (
      <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
        {!loading ? (
          <View style={styles.goneWrap}>
            <Text style={styles.goneTitle}>This quote is gone</Text>
            <Text style={styles.goneBody}>
              It may have been deleted, or the link is stale.
            </Text>
            <Pressable
              onPress={() => goBackOrTo(router, '/quotes')}
              style={({ pressed }) => [styles.goneBtn, pressed && { opacity: 0.85 }]}>
              <Text style={styles.goneBtnText}>Back to Quotes</Text>
            </Pressable>
          </View>
        ) : null}
      </SafeAreaView>
    );
  }

  const money = (cents: number) => `$${(cents / 100).toLocaleString()}`;
  const dateFmt = (ms: number) =>
    new Date(ms).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });

  const setStatus = async (s: QuoteStatus) => {
    if (quote.status === 'converted') return; // terminal state
    await updateQuote(db, quote.id, { status: s });
    await fetch();
  };

  const startEdit = () => {
    setEAmount((quote.amount_cents / 100).toFixed(2));
    setEProject(quote.project ?? '');
    setEVendor(quote.vendor ?? '');
    setEDescription(quote.description ?? '');
    setENotes(quote.notes ?? '');
    setEditing(true);
  };

  const saveEdit = async () => {
    const cents = Math.round(parseFloat(eAmount.replace(/[^0-9.]/g, '')) * 100);
    if (isNaN(cents) || cents < 0) {
      void notice({
        title: "Couldn't read the amount",
        message: 'Enter a number like 8500 or 8,500.00.',
        tone: 'warning',
      });
      return;
    }
    await updateQuote(db, quote.id, {
      amount_cents: cents,
      project: eProject.trim() || null,
      vendor: eVendor.trim() || null,
      description: eDescription.trim() || null,
      notes: eNotes.trim() || null,
    });
    setEditing(false);
    await fetch();
  };

  /** Replace (or attach) the quote's document (Wave 61, user ask) —
   *  picks any file, persists it, creates a fresh estimate Document,
   *  and points quote_doc_id at it. The old document stays in Files
   *  (it's still a record; the user can delete it there). */
  const replaceDoc = async () => {
    try {
      const res = await DocumentPicker.getDocumentAsync({ copyToCacheDirectory: true });
      if (res.canceled || !res.assets[0]) return;
      const f = res.assets[0];
      setWorking(true);
      const ext = f.name?.match(/\.[A-Za-z0-9]+$/)?.[0] ?? '';
      const fileUri = await persistPickedFile(f.uri, `quote_${Date.now()}${ext}`);
      const newDocId = await addDocument(db, {
        propertyId: quote.property_id,
        kind: 'estimate',
        title: `Estimate — ${quote.vendor ?? quote.project ?? 'quote'}`.slice(0, 80),
        file_uri: fileUri,
        mime_type: f.mimeType ?? 'application/octet-stream',
        vendor: quote.vendor ?? null,
        cost_cents: quote.amount_cents,
        notes: null,
      });
      await updateQuote(db, quote.id, { quote_doc_id: newDocId });
      await fetch();
    } catch (err) {
      void notice({ title: "Couldn't attach the file", message: String(err), tone: 'error' });
    } finally {
      setWorking(false);
    }
  };

  const onConvert = async () => {
    const ok = await confirm({
      title: 'Turn this quote into an expense?',
      message:
        `Logs ${money(quote.amount_cents)} to ${quote.vendor ?? 'this vendor'} ` +
        `as an expense dated today. The quote stays here as your price ` +
        `record — you can adjust the expense amount afterwards if the ` +
        `final bill differed.`,
      confirmLabel: 'Create expense',
    });
    if (!ok) return;
    setWorking(true);
    try {
      const expenseId = await convertQuoteToExpense(db, quote);
      router.replace(`/expenses/${expenseId}` as never);
    } finally {
      setWorking(false);
    }
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: 'Delete this quote?',
      message: quote.status === 'converted'
        ? 'The expense it became stays — only the quote record goes away.'
        : 'This removes it from your price history.',
      destructive: true,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    await deleteQuote(db, quote.id);
    goBackOrTo(router, '/quotes');
  };

  const meta = QUOTE_STATUS_META[quote.status];
  // Manual lifecycle states — converted excluded (Convert button only).
  const SETTABLE: QuoteStatus[] = ['open', 'accepted', 'declined', 'expired'];
  const expiringSoon =
    quote.status === 'open' &&
    quote.valid_until != null &&
    quote.valid_until - Date.now() < 7 * 24 * 60 * 60 * 1000 &&
    quote.valid_until > Date.now();

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/quotes')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Quote</Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          <Pressable
            onPress={editing ? () => setEditing(false) : startEdit}
            style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
            hitSlop={12}
            accessibilityLabel={editing ? 'Cancel editing' : 'Edit quote'}>
            {editing ? (
              <X size={17} color={Palette.ink} strokeWidth={1.9} />
            ) : (
              <Pencil size={15} color={Palette.forest} strokeWidth={1.9} />
            )}
          </Pressable>
          <Pressable
            onPress={onDelete}
            style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
            hitSlop={12}
            accessibilityLabel="Delete quote">
            <Trash2 size={17} color={Palette.clay} strokeWidth={1.9} />
          </Pressable>
        </View>
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 140 }} showsVerticalScrollIndicator={false}>
        {editing ? (
          <View style={styles.card}>
            <Text style={styles.cardLabel}>EDIT QUOTE</Text>
            <EditField label="Amount ($)" value={eAmount} onChangeText={setEAmount} inputMode="decimal" />
            <EditField label="Project" value={eProject} onChangeText={setEProject} placeholder="Groups competing bids" />
            {/* Project suggestion chips (Wave 72, user ask) — same
                helper the New Quote form has: existing quote groups +
                the Projects list, one tap to join a group exactly. */}
            {projectSuggestions.length > 0 ? (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                contentContainerStyle={styles.projSugRow}>
                {projectSuggestions.map((p) => (
                  <Pressable
                    key={p}
                    onPress={() => setEProject(p)}
                    style={({ pressed }) => [styles.projSugChip, pressed && { opacity: 0.85 }]}>
                    <Text style={styles.projSugText} numberOfLines={1}>{p}</Text>
                  </Pressable>
                ))}
              </ScrollView>
            ) : null}
            <EditField label="Vendor / contractor" value={eVendor} onChangeText={setEVendor} />
            <EditField label="Work description" value={eDescription} onChangeText={setEDescription} />
            <EditField label="Notes" value={eNotes} onChangeText={setENotes} multiline />
            <Pressable
              onPress={saveEdit}
              style={({ pressed }) => [styles.editSaveBtn, pressed && { transform: [{ scale: 0.98 }] }]}>
              <Check size={16} color={Palette.cream} strokeWidth={2.2} />
              <Text style={styles.editSaveText}>Save changes</Text>
            </Pressable>
          </View>
        ) : null}

        {/* Hero — amount dominates; vendor + project stack beneath. */}
        <View style={styles.hero}>
          <Text style={styles.heroAmount}>{money(quote.amount_cents)}</Text>
          <Text style={styles.heroVendor}>
            {quote.vendor ?? 'Unknown vendor'}
          </Text>
          {quote.project ? (
            <View style={styles.projectPill}>
              <Text style={styles.projectPillText}>{quote.project}</Text>
            </View>
          ) : null}
          <Text style={styles.heroMeta}>
            Quoted {dateFmt(quote.quoted_on)}
            {quote.valid_until ? ` · valid until ${dateFmt(quote.valid_until)}` : ''}
          </Text>
          {expiringSoon ? (
            <Text style={styles.expiryWarn}>Expires soon — decide before it lapses.</Text>
          ) : null}
        </View>

        {/* Status lifecycle. Converted renders as a single locked pill. */}
        <View style={styles.card}>
          <Text style={styles.cardLabel}>STATUS</Text>
          {quote.status === 'converted' ? (
            <View style={styles.convertedRow}>
              <View style={[styles.statusChip, styles.statusChipSel, { borderColor: meta.tint, backgroundColor: meta.tint }]}>
                <Text style={[styles.statusChipText, { color: Palette.cream }]}>Converted</Text>
              </View>
              {quote.converted_expense_id ? (
                <Pressable
                  onPress={() => router.push(`/expenses/${quote.converted_expense_id}` as never)}
                  style={({ pressed }) => [styles.expenseLink, pressed && { opacity: 0.7 }]}>
                  <Text style={styles.expenseLinkText}>View the expense</Text>
                  <ChevronRight size={13} color={Palette.forest} strokeWidth={2.2} />
                </Pressable>
              ) : null}
            </View>
          ) : (
            <View style={styles.statusRow}>
              {SETTABLE.map((s) => {
                const m = QUOTE_STATUS_META[s];
                const sel = quote.status === s;
                return (
                  <Pressable
                    key={s}
                    onPress={() => setStatus(s)}
                    style={({ pressed }) => [
                      styles.statusChip,
                      { borderColor: m.tint },
                      sel && { backgroundColor: m.tint },
                      pressed && { opacity: 0.85 },
                    ]}>
                    <Text style={[styles.statusChipText, { color: sel ? Palette.cream : m.tint }]}>
                      {m.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          )}
        </View>

        {/* Linked contractor — tap through to the contact card. */}
        {contact ? (
          <View style={styles.card}>
            <Text style={styles.cardLabel}>CONTRACTOR</Text>
            <Pressable
              onPress={() => router.push(`/contact/${contact.id}` as never)}
              style={({ pressed }) => [styles.docRow, pressed && { opacity: 0.85 }]}>
              <View style={styles.contactAvatar}>
                <Phone size={16} color={Palette.forest} strokeWidth={1.9} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={styles.docTitle} numberOfLines={1}>{contact.name}</Text>
                <Text style={styles.docMeta} numberOfLines={1}>
                  {[contact.role, contact.company, contact.phone]
                    .filter(Boolean)
                    .join(' · ') || 'Saved contact'}
                </Text>
              </View>
              <ChevronRight size={16} color={Palette.inkFaint} strokeWidth={1.8} />
            </Pressable>
          </View>
        ) : null}

        {/* Scanned estimate — ABOVE the detail fields (Wave 61: the
            document is what the user came to see; it was buried below
            the fold). */}
        {doc ? (
          <View style={styles.card}>
            <View style={styles.docHead}>
              <Text style={[styles.cardLabel, { marginBottom: 0 }]}>SCANNED ESTIMATE</Text>
              <Pressable
                onPress={() => void replaceDoc()}
                disabled={working}
                hitSlop={8}
                style={({ pressed }) => [pressed && { opacity: 0.7 }]}>
                <Text style={styles.replaceLink}>{working ? 'Working…' : 'Replace'}</Text>
              </Pressable>
            </View>
            <Pressable
              onPress={() => setPreviewOpen(true)}
              style={({ pressed }) => [styles.docCard, pressed && { opacity: 0.9 }]}>
              {doc.mime_type?.includes('pdf') ? (
                <PdfThumb
                  uri={doc.file_uri}
                  style={styles.docHero}
                  fallback={
                    <View style={[styles.docHero, styles.docHeroFallback]}>
                      <FileText size={40} color={Palette.forest} strokeWidth={1.4} />
                    </View>
                  }
                />
              ) : (
                <Image source={{ uri: doc.file_uri }} style={styles.docHero} resizeMode="cover" />
              )}
              <View style={styles.docMetaRow}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.docTitle} numberOfLines={1}>{doc.title}</Text>
                  <Text style={styles.docMeta}>Tap to view, download, or share</Text>
                </View>
                <FileText size={16} color={Palette.inkFaint} strokeWidth={1.8} />
              </View>
            </Pressable>
          </View>
        ) : (
          <View style={styles.card}>
            <Text style={styles.cardLabel}>ESTIMATE DOCUMENT</Text>
            <Pressable
              onPress={() => void replaceDoc()}
              disabled={working}
              style={({ pressed }) => [styles.attachBtn, pressed && { opacity: 0.85 }]}>
              <FileText size={15} color={Palette.forest} strokeWidth={1.9} />
              <Text style={styles.attachBtnText}>
                {working ? 'Attaching…' : 'Attach the written estimate'}
              </Text>
            </Pressable>
          </View>
        )}

        {/* Detail fields — Category + Work share a row (Wave 61 mobile
            compaction); Notes always renders so the section is
            discoverable even when empty. */}
        <View style={styles.card}>
          <View style={styles.detailPair}>
            <View style={[styles.detailRow, styles.detailHalf]}>
              <Text style={styles.detailLabel}>CATEGORY</Text>
              <Text style={styles.detailValue}>{quote.category}</Text>
            </View>
            {quote.description ? (
              <View style={[styles.detailRow, styles.detailHalf]}>
                <Text style={styles.detailLabel}>WORK</Text>
                <Text style={styles.detailValue}>{quote.description}</Text>
              </View>
            ) : null}
          </View>
          {quote.notes ? (
            <DetailRow label="Notes" value={quote.notes} />
          ) : (
            <Pressable onPress={startEdit} style={styles.detailRow}>
              <Text style={styles.detailLabel}>NOTES</Text>
              <Text style={styles.notesEmpty}>Add notes — tap to edit</Text>
            </Pressable>
          )}
        </View>
      </ScrollView>

      <DocumentPreview
        doc={previewOpen ? doc : null}
        onClose={() => setPreviewOpen(false)}
      />

      {quote.status !== 'converted' ? (
        <StickyBottomBar>
          <Pressable
            onPress={onConvert}
            disabled={working}
            style={({ pressed }) => [
              styles.convertBtn,
              { flex: 1 },
              working && { opacity: 0.5 },
              pressed && { transform: [{ scale: 0.98 }] },
            ]}>
            <ArrowRightLeft size={18} color={Palette.cream} strokeWidth={2} />
            <Text style={styles.convertText}>
              {working ? 'Converting…' : 'Turn into an expense'}
            </Text>
          </Pressable>
        </StickyBottomBar>
      ) : null}
    </SafeAreaView>
  );
}

function EditField({
  label, value, onChangeText, placeholder, inputMode, multiline,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  inputMode?: 'decimal';
  multiline?: boolean;
}) {
  return (
    <View style={{ marginBottom: Spacing.three }}>
      <Text style={styles.detailLabel}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        inputMode={inputMode}
        multiline={multiline}
        style={[
          styles.editInput,
          webNoOutline,
          multiline && { height: 70, textAlignVertical: 'top' },
        ]}
      />
    </View>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.detailRow}>
      <Text style={styles.detailLabel}>{label.toUpperCase()}</Text>
      <Text style={styles.detailValue}>{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  // Compact hero (Wave 61) — trimmed padding + type so the estimate
  // preview fits above the fold on phones.
  hero: {
    marginTop: Spacing.two, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, paddingVertical: Spacing.three,
    paddingHorizontal: Spacing.four,
    alignItems: 'center', gap: 2,
  },
  heroAmount: {
    fontFamily: Fonts.serifBold, fontSize: 32, color: Palette.forest,
    letterSpacing: -0.8,
  },
  heroVendor: {
    fontFamily: Fonts.sansBold, fontSize: 16, color: Palette.ink,
    letterSpacing: -0.1,
  },
  projectPill: {
    marginTop: 2,
    paddingHorizontal: 12, paddingVertical: 5, borderRadius: 999,
    backgroundColor: '#EAF0EA',
    borderWidth: 1, borderColor: Palette.line,
  },
  projectPillText: {
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.forest,
    letterSpacing: 0.2,
  },
  heroMeta: {
    marginTop: 4,
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
  },
  expiryWarn: {
    marginTop: 2,
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.honeyDeep,
  },
  card: {
    marginTop: Spacing.three, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  cardLabel: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.inkFaint,
    letterSpacing: 0.6, marginBottom: 8,
  },
  statusRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  statusChip: {
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999,
    borderWidth: 1.5,
  },
  statusChipSel: {},
  statusChipText: {
    fontFamily: Fonts.sansBold, fontSize: 13, letterSpacing: 0.2,
  },
  convertedRow: { flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  expenseLink: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
  },
  expenseLinkText: {
    fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.forest,
  },
  detailRow: {
    paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: Palette.line,
  },
  detailLabel: {
    fontFamily: Fonts.sansBold, fontSize: 11, color: Palette.inkFaint,
    letterSpacing: 0.5,
  },
  detailValue: {
    marginTop: 2,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
    lineHeight: 20,
  },
  docRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
  },
  docCard: {
    borderRadius: 14,
    borderWidth: 1, borderColor: Palette.line,
    overflow: 'hidden',
    backgroundColor: Palette.card,
  },
  docHero: {
    width: '100%', height: 230, backgroundColor: '#fff',
  },
  docHead: {
    flexDirection: 'row', alignItems: 'center',
    justifyContent: 'space-between', marginBottom: 8,
  },
  replaceLink: {
    fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest,
    textDecorationLine: 'underline',
  },
  attachBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 7,
    borderWidth: 1.5, borderColor: Palette.forest, borderStyle: 'dashed',
    borderRadius: 12, paddingVertical: 12,
    backgroundColor: '#EEF3EE',
  },
  attachBtnText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest },
  detailPair: { flexDirection: 'row', gap: 12 },
  detailHalf: { flex: 1, minWidth: 0 },
  notesEmpty: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkFaint,
    fontStyle: 'italic',
  },
  docHeroFallback: {
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#EAF0EA',
  },
  docMetaRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    padding: 12,
    borderTopWidth: 1, borderTopColor: Palette.line,
  },
  docTitle: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  docMeta: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
  convertBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, backgroundColor: Palette.forest,
    paddingVertical: 16, borderRadius: 18,
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
  },
  convertText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },

  contactAvatar: {
    width: 40, height: 40, borderRadius: 12,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },

  projSugRow: { gap: 6, paddingVertical: 8 },
  projSugChip: {
    maxWidth: 220,
    paddingHorizontal: 12, paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: '#EEF3EE',
    borderWidth: 1.2, borderColor: 'rgba(44,71,56,0.3)',
  },
  projSugText: { fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.forest },
  editInput: {
    marginTop: 4, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 10,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  editSaveBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 7, backgroundColor: Palette.forest,
    paddingVertical: 13, borderRadius: 13,
  },
  editSaveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 14 },

  goneWrap: {
    flex: 1, alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: Spacing.six, gap: 6,
  },
  goneTitle: { fontFamily: Fonts.serif, fontSize: 22, color: Palette.ink },
  goneBody: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    textAlign: 'center',
  },
  goneBtn: {
    marginTop: Spacing.three,
    paddingHorizontal: 18, paddingVertical: 11, borderRadius: 12,
    backgroundColor: Palette.forest,
  },
  goneBtnText: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.cream },
});
